import React, { useEffect, useState } from 'react'
import { CssBaseline, ThemeProvider, Theme, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import Env from '../../shared/config/envConfig'
import appConfig from '../../shared/config/appConfig'
import editConfig from '../../shared/config/editConfig'
import { getAppTheme } from '../../shared/config/getAppTheme'
import SnackbarComponent from '../../shared/components/MessageComponents/SnackbarComponent'
import { getCrudeAppName, getPrefersDarkMode, getRedirectPath, getUser } from '../../store/settings/settings.selectors'
import { Route, Switch } from 'react-router'
import TableActionButtons from './TableActionButtons'

// Import CRUDE
import { CrudeConfiguration } from '@3m5/crude-frontend/dist/shared/types/configurationTypes'
import { useCrude, CrudeContext } from '@3m5/crude-frontend/dist/crudeHooks'
import { crudeRootReducer } from '@3m5/crude-frontend/dist/store/crudeRoot.reducer'

import Login from '../../shared/components/Login'
import EventRegistration from './EventRegistration'
import ConfirmRegistration from './ConfirmRegistration'
import Reports from './Reports'
import { useHistory } from 'react-router-dom'
import { setUser } from '../../store/settings/settings.actions'
import HeaderComponent from './Header/HeaderComponent'
import Settings from './Settings'
import { createStore } from 'redux'
import crudeTabConfig from '../../shared/config/crudeTabConfig'
import Dashboard from './Dashboard'
import Invoice from './Invoice'
import EventInvoice from './EventInvoice'
import Attendance from './Attendance'
import { addCsrfHeaders, CSRF_HEADER, getCsrfToken } from '../../shared/config/csrf'
import crudeLicense from '../../shared/config/crudeLicense'

const App: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const store2 = createStore(
    crudeRootReducer,
  )

  const user = useSelector(getUser)
  const darkMode = useSelector(getPrefersDarkMode)
  const redirectPath = useSelector(getRedirectPath)

  const [theme, setTheme] = useState<Theme>(getAppTheme(darkMode))
  const appName: string = useSelector(getCrudeAppName)

  useEffect(() => {
    const oldMode = localStorage.getItem('darkMode') === 'true'
    store2.getState().crudeStore.darkMode = darkMode
    setTheme(getAppTheme(darkMode))
    if (oldMode !== darkMode) {
      localStorage.setItem('darkMode', darkMode ? 'true' : 'false')
      location.reload()
    }
  },
  [darkMode],
  )

  const [token, setToken] = useState<string | null>(null)

  useEffect(() => {
    const actualPath = history.location.pathname
    setToken(null)
    let pathname = '/'

    if (actualPath.search('confirmation') !== -1) {
      pathname = 'confirmation'
    } else if (actualPath.search('confirmRegistration') !== -1) {
      pathname = 'confirmRegistration'
    } else if (actualPath.search('registration') !== -1) {
      pathname = 'registration'
    } else if (actualPath.search('thankyou') !== -1) {
      pathname = 'thankyou'
    } else if (!user) {
      pathname = 'login'
    } else {
      if (redirectPath) {
        history.push(redirectPath)
      }
    }

    switch (pathname) {
      case 'login':
        if (actualPath !== '/login') history.push('/login')
        break
      case 'confirmation':
        setToken(history.location.pathname.replace('confirmation/', '').replace('/', ''))
        break
      case 'registration':
        if (actualPath !== '/registration') history.push('/registration')
        break
      case 'confirmRegistration':
        if (actualPath !== '/confirmRegistration') history.push('/confirmRegistration')
        break
      case 'thankyou':
        if (actualPath !== '/thankyou') history.push('/thankyou')
        break
      default:
        if (actualPath !== '/') history.push('/')
        break
    }
  }, [history, user],
  )

  const crudeConfiguration: CrudeConfiguration = {
    theme: theme,
    path: `/${appName}`,
    api: {
      app: appName,
      restPath: `${Env.crudeRestBase}/api/crude/`,
      headers: [
        {
          name: CSRF_HEADER,
          getHeader: (): string => getCsrfToken(),
        },
      ],
    },
    appLayout: appConfig(),
    editLayout: editConfig(),
    tabs: crudeTabConfig(),
    crudeLicense: crudeLicense(),
  }

  const basePath = Env.basePath
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  addCsrfHeaders(requestHeaders)
  const onLogout = () => {
    const restUrl = `${basePath}/api/session/logout`
    fetch(restUrl,
      {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(() => dispatch(setUser(undefined)))
      .catch(err => console.log(err))
  }

  useEffect(() => {
    const restUrl = `${basePath}/api/session/user`
    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(json => {
        !json.error && dispatch(setUser(json))
      })
      .catch(err => console.log(err))
  },
  [],
  )

  window.addEventListener('CrudeLogout', (() => {
    onLogout()
  }) as EventListener)

  const DashboardApp = () => (<Dashboard />)
  const SettingsApp = () => (<Settings />)
  const InvoiceApp = () => (<Invoice />)
  const EventInvoiceApp = () => (<EventInvoice />)
  const AttendanceApp = () => (<Attendance />)
  const ReportsApp = () => (<Reports />)

  const crudeContext = useCrude({ appPageProps: crudeConfiguration, tableActionComponent: TableActionButtons })
  return (
    <>
      <CrudeContext.Provider value={crudeContext}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Stack spacing={2} sx={{ width: '100%', padding: (history.location.pathname === '/registration' || history.location.pathname === '/confirmRegistration') ? 0 : '0 20px' }}>
            <div className='main'>
              <HeaderComponent doLogout={() => onLogout()} />
              <Switch>
                <Route exact path='/login' component={Login} />
                <Route exact path='/registration' component={EventRegistration} />
                <Route exact path='/confirmRegistration' component={ConfirmRegistration} />
                {user && !token &&
                  <>
                    <Route exact path='/' component={DashboardApp} />
                    <Route exact path='/settings' component={SettingsApp} />
                    <Route exact path='/invoice' component={InvoiceApp} />
                    <Route exact path='/invoice/:invoiceId' component={InvoiceApp} />
                    <Route exact path='/event-invoice' component={EventInvoiceApp} />
                    <Route exact path='/event-invoice/:invoiceId' component={EventInvoiceApp} />
                    <Route exact path='/attendance' component={AttendanceApp} />
                    <Route exact path='/reports' component={ReportsApp} />
                    <Route key='crude' path={`/${appName}`} component={crudeContext.Crude} />
                  </>}
              </Switch>
            </div>
            {TableActionButtons}
            <SnackbarComponent />
          </Stack>
        </ThemeProvider>
      </CrudeContext.Provider>
    </>
  )
}

export default App
